/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Flame, MessageSquare, Send, AlertTriangle, RefreshCw, Mail,
  TrendingUp, Sparkles, Target, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 60_000;

const formatInr = (n) => {
  if (!n || Number.isNaN(Number(n))) return '₹0';
  const num = Math.round(Number(n));
  const s = num.toLocaleString('en-IN');
  return `₹${s}`;
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const classificationBadge = {
  interested: { color: 'bg-rose-100 text-rose-700 border-rose-300', icon: Flame, label: 'Interested' },
  objection: { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: MessageSquare, label: 'Objection' },
  opt_out: { color: 'bg-slate-200 text-slate-700 border-slate-300', icon: Target, label: 'Opt-out' },
  spam: { color: 'bg-slate-100 text-slate-500 border-slate-200', icon: Target, label: 'Spam' },
};

const tierBadge = {
  premium: { color: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white', icon: Sparkles, label: 'Premium' },
  growing: { color: 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white', icon: TrendingUp, label: 'Growing' },
  small: { color: 'bg-slate-200 text-slate-700', icon: Target, label: 'Local' },
};

const TileCard = ({ title, value, subtitle, icon: Icon, tone = 'emerald', testid }) => {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50/50',
    rose: 'border-rose-200 bg-rose-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    slate: 'border-slate-200 bg-slate-50/50',
  };
  const iconTones = {
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
    amber: 'bg-amber-100 text-amber-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  return (
    <Card className={`border-2 ${tones[tone]}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
            <p className="text-3xl font-extrabold text-slate-900 mt-2" data-testid={`${testid}-value`}>{value}</p>
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
          </div>
          <div className={`p-2.5 rounded-lg ${iconTones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function LeadIntelDashboard() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingPulse, setSendingPulse] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const pollRef = useRef(null);

  const loadSnapshot = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get('/super-admin/lead-intel/pulse?window_hours=24');
      setSnapshot(data);
      setLastRefreshed(new Date());
    } catch (err) {
      if (!silent) toast.error('Failed to refresh pulse snapshot');
      // Silent failure on auto-poll — backend may be redeploying.
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSnapshot();
    pollRef.current = setInterval(() => loadSnapshot(true), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadSnapshot]);

  const handleSendPulseNow = async () => {
    setSendingPulse(true);
    try {
      const { data } = await api.post('/super-admin/lead-intel/send-pulse-now');
      toast.success(
        `Daily Pulse sent · ${data.sent}/${data.recipients?.length || 0} super-admins`,
      );
      loadSnapshot();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dispatch Daily Pulse');
    } finally {
      setSendingPulse(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-5">
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600">No snapshot available.</p>
        <Button className="mt-4" onClick={() => loadSnapshot()} data-testid="retry-load-btn">
          Try again
        </Button>
      </div>
    );
  }

  const o = snapshot.phase317_outreach;
  const t = snapshot.phase318_triage;
  const n = snapshot.phase319_nurture;
  const s = snapshot.phase108_stalls;
  const interested = t.by_classification.interested || 0;
  const objections = t.by_classification.objection || 0;
  const optOuts = t.by_classification.opt_out || 0;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="lead-intel-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            Lead Intel · Founder's Pulse
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Live snapshot of the autonomous acquisition flywheel · auto-refreshes every 60s
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <Clock className="h-4 w-4" />
          <span>
            Last refreshed:{' '}
            <strong className="text-slate-700">
              {lastRefreshed ? relativeTime(lastRefreshed.toISOString()) : '—'}
            </strong>
          </span>
          <Button
            size="sm" variant="outline"
            onClick={() => loadSnapshot()}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleSendPulseNow}
            disabled={sendingPulse}
            data-testid="send-pulse-btn"
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
          >
            <Mail className={`h-4 w-4 mr-1.5 ${sendingPulse ? 'animate-pulse' : ''}`} />
            {sendingPulse ? 'Sending...' : 'Email Pulse Now'}
          </Button>
        </div>
      </div>

      {/* 4 KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <TileCard
          title="Outreach dispatched"
          value={o.count.toLocaleString()}
          subtitle={`${o.by_tier.premium} premium · ${o.by_tier.growing} growing · ${o.by_tier.small} local`}
          icon={Send}
          tone="emerald"
          testid="tile-outreach"
        />
        <TileCard
          title="Interested replies"
          value={interested.toLocaleString()}
          subtitle={
            objections + optOuts > 0
              ? `${objections} objections · ${optOuts} opt-outs`
              : 'Bring on the flywheel'
          }
          icon={Flame}
          tone={interested > 0 ? 'rose' : 'slate'}
          testid="tile-interested"
        />
        <TileCard
          title="Re-engagement nurtures"
          value={n.count.toLocaleString()}
          subtitle={`${n.email_sent} email · ${n.whatsapp_sent} WhatsApp`}
          icon={MessageSquare}
          tone="emerald"
          testid="tile-nurture"
        />
        <TileCard
          title="ARR at risk"
          value={formatInr(s.arr_at_risk_inr)}
          subtitle={`${s.open_count} open stalls`}
          icon={AlertTriangle}
          tone={s.arr_at_risk_inr > 0 ? 'rose' : 'slate'}
          testid="tile-stalls"
        />
      </div>

      {/* Two-column: Top Interested + Top Objections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-rose-200" data-testid="top-interested-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Flame className="h-4 w-4 text-rose-600" />
              Top Interested Replies (last 24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(t.top_interested?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-interested">
                No interested replies in this window.
              </p>
            ) : (
              <div className="space-y-2.5">
                {t.top_interested.slice(0, 5).map((r, idx) => {
                  const TBadge = tierBadge[r.tier] || tierBadge.small;
                  const TIcon = TBadge.icon;
                  return (
                    <div
                      key={r.lead_id || idx}
                      className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-rose-300 transition-colors"
                      data-testid={`interested-row-${idx}`}
                    >
                      <Badge className={`${TBadge.color} text-xs whitespace-nowrap`}>
                        <TIcon className="h-3 w-3 mr-1" />
                        {TBadge.label}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">
                          {r.academy_name || '—'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {r.city || '—'} · {relativeTime(r.logged_at)}
                        </p>
                        {r.summary && (
                          <p className="text-xs text-slate-600 italic mt-1 line-clamp-2">
                            "{r.summary}"
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-amber-200" data-testid="top-objections-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4 text-amber-600" />
              Top Objections (last 24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(t.top_objections?.length || 0) === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-objections">
                No objections in this window.
              </p>
            ) : (
              <div className="space-y-2.5">
                {t.top_objections.slice(0, 5).map((r, idx) => {
                  const TBadge = tierBadge[r.tier] || tierBadge.small;
                  const TIcon = TBadge.icon;
                  return (
                    <div
                      key={r.lead_id || idx}
                      className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                      data-testid={`objection-row-${idx}`}
                    >
                      <Badge className={`${TBadge.color} text-xs whitespace-nowrap`}>
                        <TIcon className="h-3 w-3 mr-1" />
                        {TBadge.label}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">
                          {r.academy_name || '—'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {r.city || '—'} · {relativeTime(r.logged_at)}
                        </p>
                        {r.summary && (
                          <p className="text-xs text-slate-600 italic mt-1 line-clamp-2">
                            "{r.summary}"
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stalls leaderboard */}
      <Card className="border-amber-200" data-testid="top-stalls-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Stall Detector — Top 5 leaking ARR
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(s.top_stalls?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-stalls">
              No open stalls — pipeline is healthy.
            </p>
          ) : (
            <div className="space-y-2">
              {s.top_stalls.slice(0, 5).map((r, idx) => {
                const tier = r.tier || 'small';
                const TBadge = tierBadge[tier];
                const TIcon = TBadge.icon;
                const arr = r.estimated_arr_value
                  || ({ premium: 35000, growing: 25000, small: 10000 }[tier]);
                return (
                  <div
                    key={r.name + idx}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                    data-testid={`stall-row-${idx}`}
                  >
                    <Badge className={`${TBadge.color} text-xs whitespace-nowrap`}>
                      <TIcon className="h-3 w-3 mr-1" />
                      {TBadge.label}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        {r.name || '—'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {Math.floor(r.days_stalled || 0)}d in {r.pipeline_stage} · {r.city || '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-rose-600">{formatInr(arr)}</p>
                      <p className="text-xs text-slate-400">at risk</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent activity feed */}
      <Card data-testid="recent-activity-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-slate-500" />
            Recent activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(snapshot.recent_activity?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-activity">
              No activity in the last 24h.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {snapshot.recent_activity.map((a, idx) => {
                const cBadge = a.classification ? classificationBadge[a.classification] : null;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 py-2 px-2 rounded hover:bg-slate-50 transition-colors text-sm"
                    data-testid={`activity-row-${idx}`}
                  >
                    <span className="text-[11px] text-slate-400 whitespace-nowrap mt-0.5 w-16">
                      {relativeTime(a.at)}
                    </span>
                    {a.type === 'outreach' && (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                        Outreach
                      </Badge>
                    )}
                    {a.type === 'triage' && cBadge && (
                      <Badge className={`${cBadge.color} text-[10px]`}>{cBadge.label}</Badge>
                    )}
                    {a.type === 'nurture' && (
                      <Badge variant="outline" className="text-[10px] border-teal-300 text-teal-700">
                        Nurture
                      </Badge>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{a.academy_name || '—'}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {a.city ? `${a.city} · ` : ''}{a.summary || a.detail || ''}
                        {a.case_study_academy && ` · case study: ${a.case_study_academy}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
